'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import Image from 'next/image'
import { type FC, type ReactElement } from 'react'

import { IconArrowRight } from '@redteclab/icons'

import { Link } from 'base-ui'

import { type StoryblokComponentProps } from '../../model'
import { type StoryblokListButtonType } from '../../storyblok'
import { useStoryblokWebOrAppLink } from '../../useStoryblokLink'
import { StoryblokUnmappedIcon } from '../StoryblokIcon/StoryblokUnmapedIcon'

import { StoryblokListButtonBrandIcon } from './StoryblokListButtonBrandIcon'

import styles from './StoryblokListButton.module.css'

const getListButtonStartAdornment = (
  blok: StoryblokListButtonType,
): ReactElement | null => {
  switch (blok.startAdornmentType) {
    case 'brandIcon': {
      return (
        <StoryblokListButtonBrandIcon icon={blok.startAdornmentBrandIcon} />
      )
    }
    case 'icon': {
      return (
        <StoryblokUnmappedIcon
          className={styles['storyblok-list-button__icon-start-adornment']}
          icon={blok.startAdornmentIcon}
        />
      )
    }
    case 'image': {
      return blok.startAdornmentImageSrc ? (
        <div className={styles['storyblok-list-button__image-start-adornment']}>
          <Image
            alt=""
            className="object-cover"
            fill
            src={blok.startAdornmentImageSrc}
            unoptimized
          />
        </div>
      ) : null
    }
    default: {
      return null
    }
  }
}

export const StoryblokListButton: FC<
  StoryblokComponentProps<StoryblokListButtonType>
> = ({ blok }) => {
  const { appHref, href, text } = blok
  const { target, url } = useStoryblokWebOrAppLink({
    appLink: appHref,
    link: href,
  })

  return (
    <div
      className={styles['storyblok-list-button']}
      {...storyblokEditable(blok)}
    >
      {getListButtonStartAdornment(blok)}
      <Link
        className={styles['storyblok-list-button__link']}
        href={url}
        overlay
        rel={target === '_blank' ? 'noreferrer' : undefined}
        target={target}
      >
        {text}
      </Link>
      <IconArrowRight className="mr-4 size-6 shrink-0 text-dark-brand" />
    </div>
  )
}
